import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, GridOptions, RowClassParams, CellClickedEvent } from 'ag-grid-community';
import { DayStat, HourlyDataPoint } from '../version.service';
import { formatKwh, fmtDate, fmtMonth, fmtMonthShort } from '../shared/format.utils';

@Component({
  selector: 'app-statistik',
  templateUrl: './statistik.component.html',
})
export class StatistikComponent implements OnChanges, OnInit, OnDestroy {
  @Input() stats:   DayStat[] = [];
  @Input() loading  = false;
  @Input() darkMode = false;

  private _langSub?: Subscription;

  readonly Math = Math;
  statsMonthFilter     = 'all';
  statsView: 'daily' | 'monthly' = 'daily';
  globalYearFilter     = 'all';

  statsColDefs:        ColDef[] = [];
  monthlyStatsColDefs: ColDef[] = [];

  readonly SYSTEM_COST_EUR   = 25_000;
  readonly ELECTRICITY_PRICE = 0.30;
  readonly FEEDIN_TARIFF     = 0.082;
  readonly CO2_FACTOR        = 0.380;

  readonly statsGridOptions: GridOptions = {
    rowHeight: 32,
    headerHeight: 36,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    tooltipShowDelay: 300,
    enableBrowserTooltips: true,
    rowStyle: { cursor: 'pointer' },
    rowClassRules: {
      'row-fault': (p: RowClassParams) => !!p.data?.faultReason,
    },
    onFirstDataRendered: (e) => { e.api.autoSizeAllColumns(); },
    onCellClicked: (e: CellClickedEvent) => this.openHourly(e.data?.date, e.colDef.field ?? null),
    onCellDoubleClicked: (e) => {
      const val = e.value != null ? String(e.value) : '';
      navigator.clipboard.writeText(val).catch(() => {});
    },
  };

  // ── Hourly chart overlay ────────────────────────────────────────────────
  /** Fields that map to a dedicated dataset in the hourly chart. */
  private static readonly HOURLY_DATASET_FIELDS = new Set([
    'generationKwh', 'consumptionKwh', 'gridImportKwh', 'gridExportKwh',
  ]);

  hourlyDate:    string | null      = null;
  /** null = all datasets; otherwise the field key of the single dataset to show. */
  hourlyField:   string | null      = null;
  hourlyData:    HourlyDataPoint[]  = [];
  hourlyLoading  = false;
  hourlyError    = false;
  private readonly _hourlyCache = new Map<string, HourlyDataPoint[]>();

  openHourly(date: string | undefined, field: string | null): void {
    if (!date) return;
    this.hourlyDate  = date;
    this.hourlyField = field && StatistikComponent.HOURLY_DATASET_FIELDS.has(field) ? field : null;
    this.hourlyError = false;

    if (this._hourlyCache.has(date)) {
      this.hourlyData    = this._hourlyCache.get(date)!;
      this.hourlyLoading = false;
      return;
    }

    this.hourlyLoading = true;
    this.hourlyData    = [];
    fetch(`/api/hourly?date=${date}`)
      .then(r => r.json())
      .then((data: HourlyDataPoint[]) => {
        this._hourlyCache.set(date, data);
        if (this.hourlyDate === date) {
          this.hourlyData    = data;
          this.hourlyLoading = false;
        }
      })
      .catch(() => {
        if (this.hourlyDate === date) {
          this.hourlyError   = true;
          this.hourlyLoading = false;
        }
      });
  }

  closeHourly(): void { this.hourlyDate = null; }

  hourlyFieldLabel(field: string | null): string {
    const labels: Record<string, string> = {
      generationKwh:  '☀️ Ertrag',
      consumptionKwh: '⚡ Verbrauch',
      gridImportKwh:  '🔌 Netzbezug',
      gridExportKwh:  '↑ Einspeisung',
    };
    return field ? (labels[field] ?? '') : '';
  }

  constructor(readonly translate: TranslateService) {}

  ngOnInit(): void {
    this.buildColDefs();
    this._langSub = this.translate.onLangChange.subscribe(() => this.buildColDefs());
  }

  ngOnDestroy(): void {
    this._langSub?.unsubscribe();
  }

  ngOnChanges(_changes: SimpleChanges): void {}

  buildColDefs(): void {
    const t        = (key: string) => this.translate.instant(key);
    const faultTip = (p: any): string | null => p.data?.faultReason
      ? this.translate.instant('FAULT_REASON.' + p.data.faultReason)
      : null;

    this.statsColDefs = [
      {
        field: 'date', headerName: t('COLS.TAG'), width: 120, sortable: true, sort: 'desc',
        valueFormatter: p => fmtDate(p.value, this.translate.currentLang),
        tooltipValueGetter: faultTip,
      },
      {
        field: 'generationKwh', headerName: t('COLS.ERTRAG'), width: 110, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-warning',
        tooltipValueGetter: faultTip,
      },
      {
        field: 'consumptionKwh', headerName: t('COLS.VERBRAUCH'), width: 120, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-primary',
        tooltipValueGetter: faultTip,
      },
      {
        field: 'gridImportKwh', headerName: t('COLS.BEZUG'), width: 100, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-danger',
        tooltipValueGetter: faultTip,
      },
      {
        field: 'autarkiePercent', headerName: t('COLS.AUTARKIE'), width: 100, sortable: true,
        valueFormatter: p => p.value != null ? Math.floor(p.value || 0) + ' %' : '–',
        tooltipValueGetter: faultTip,
        cellClassRules: {
          'text-success fw-bold': p => p.value >= 80,
          'text-warning fw-bold': p => p.value >= 50 && p.value < 80,
          'text-danger fw-bold':  p => p.value < 50,
        },
      },
      {
        field: 'gridExportKwh', headerName: t('COLS.EINSPEISUNG'), width: 120, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-success',
        tooltipValueGetter: faultTip,
      },
      {
        field: 'sunriseUtc', headerName: t('COLS.SONNENAUFGANG'), width: 110, sortable: true,
        valueFormatter: p => p.value ? p.value + ' UTC' : '–',
        cellClass: 'text-end text-warning',
        tooltipValueGetter: faultTip,
      },
      {
        field: 'sunsetUtc', headerName: t('COLS.SONNENUNTERGANG'), width: 110, sortable: true,
        valueFormatter: p => p.value ? p.value + ' UTC' : '–',
        cellClass: 'text-end text-danger',
        tooltipValueGetter: faultTip,
      },
    ];

    this.monthlyStatsColDefs = [
      {
        field: 'date', headerName: t('COLS.MONAT'), width: 130, sortable: true, sort: 'desc',
        comparator: (a: string, b: string) => a < b ? -1 : a > b ? 1 : 0,
        valueFormatter: p => {
          if (!p.value) return p.value;
          const d = new Date(p.value + '-01');
          const locale = this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
          return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
        },
      },
      {
        field: 'generationKwh', headerName: t('COLS.ERTRAG'), width: 110, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-warning',
      },
      {
        field: 'consumptionKwh', headerName: t('COLS.VERBRAUCH'), width: 120, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-primary',
      },
      {
        field: 'gridImportKwh', headerName: t('COLS.BEZUG'), width: 100, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-danger',
      },
      {
        field: 'autarkiePercent', headerName: t('COLS.AUTARKIE'), width: 100, sortable: true,
        valueFormatter: p => p.value != null ? Math.floor(p.value || 0) + ' %' : '–',
        cellClassRules: {
          'text-success fw-bold': p => p.value >= 80,
          'text-warning fw-bold': p => p.value >= 50 && p.value < 80,
          'text-danger fw-bold':  p => p.value < 50,
        },
      },
      {
        field: 'gridExportKwh', headerName: t('COLS.EINSPEISUNG'), width: 120, sortable: true,
        valueFormatter: p => formatKwh(p.value),
        cellClass: 'text-end text-success',
      },
    ];
  }

  statsGridHeight(rows: number): number {
    return Math.min(rows * 32 + 38, 600);
  }

  /** Year-filtered; includes faulty days (shown strikethrough in table). */
  private get statsBase(): DayStat[] {
    return this.globalYearFilter === 'all'
      ? this.stats
      : this.stats.filter(s => s.date.startsWith(this.globalYearFilter));
  }

  /** Year-filtered; faulty days excluded (used for statistics + monthly aggregation). */
  private get statsBaseClean(): DayStat[] {
    return this.statsBase.filter(s => !s.faultReason);
  }

  get globalStatYears(): string[] {
    const years = [...new Set(this.stats.map(s => s.date.slice(0, 4)))];
    return years.sort((a, b) => b.localeCompare(a));
  }

  get statsMonths(): string[] {
    const months = [...new Set(this.statsBase.map(s => s.date.slice(0, 7)))];
    return months.sort((a, b) => b.localeCompare(a));
  }

  get filteredStats(): DayStat[] {
    if (this.statsMonthFilter === 'all') return this.statsBase;
    return this.statsBase.filter(s => s.date.startsWith(this.statsMonthFilter));
  }

  get monthlyStats(): DayStat[] {
    const map = new Map<string, { gen: number; cons: number; imp: number; exp: number }>();
    for (const s of this.statsBaseClean) {
      const ym = s.date.slice(0, 7);
      const e  = map.get(ym) ?? { gen: 0, cons: 0, imp: 0, exp: 0 };
      e.gen  += s.generationKwh;
      e.cons += s.consumptionKwh;
      e.imp  += s.gridImportKwh;
      e.exp  += s.gridExportKwh;
      map.set(ym, e);
    }
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([ym, v]) => ({
        date:             ym,
        generationKwh:    v.gen,
        consumptionKwh:   v.cons,
        gridImportKwh:    v.imp,
        gridExportKwh:    v.exp,
        autarkiePercent:  v.cons > 0 ? Math.max(0, (1 - v.imp / v.cons) * 100) : 0,
      } as DayStat));
  }

  /** Faulty days in the current year filter (all days, not just clean). */
  get faultDays() {
    const faults = this.statsBase.filter(s => !!s.faultReason);
    const wifi   = faults.filter(s => s.faultReason === 'WIFI_OUTAGE').length;
    const tigo   = faults.filter(s => s.faultReason === 'TIGO_OUTAGE').length;
    const freeze = faults.filter(s => s.faultReason === 'DEYE_FREEZE').length;
    const other  = faults.filter(s => s.faultReason === 'DATA_MISSING').length;
    return { total: faults.length, wifi, tigo, freeze, other };
  }

  get globalStats() {
    const base = this.statsBaseClean;
    if (!base.length) return null;
    const nz = (n: number) => n || 0;
    const gen  = nz(base.reduce((s, d) => s + d.generationKwh,  0));
    const cons = nz(base.reduce((s, d) => s + d.consumptionKwh, 0));
    const imp  = nz(base.reduce((s, d) => s + d.gridImportKwh,  0));
    const exp  = nz(base.reduce((s, d) => s + d.gridExportKwh,  0));
    const selfConsumed  = Math.max(0, gen - exp);
    const autarkie      = cons > 0 ? Math.max(0, Math.min(100, (1 - imp / cons) * 100)) : 0;
    const co2Saved      = nz(gen * this.CO2_FACTOR / 1000);
    const savings       = nz(selfConsumed * this.ELECTRICITY_PRICE);
    const feedinRevenue = nz(exp * this.FEEDIN_TARIFF);
    const totalBenefit  = nz(savings + feedinRevenue);
    const days          = base.length;
    const amortisation  = this.globalYearFilter === 'all' ? totalBenefit / this.SYSTEM_COST_EUR * 100 : null;
    const annualBenefit = days > 0 ? totalBenefit / days * 365 : 0;
    const paybackYears  = this.globalYearFilter === 'all' && annualBenefit > 0 ? this.SYSTEM_COST_EUR / annualBenefit : null;
    const dates = base.map(s => s.date).sort();
    const since = dates[0] ?? null;
    const until = dates[dates.length - 1] ?? null;
    return { gen, cons, imp, exp, selfConsumed, autarkie, co2Saved,
             savings, feedinRevenue, totalBenefit, days, amortisation,
             paybackYears, annualBenefit, since, until };
  }

  setGlobalYear(year: string): void {
    this.globalYearFilter = year;
    this.statsMonthFilter = 'all';
  }

  /** Clamp to ≥ 0 and strip −0 before toFixed(). */
  p(n: number): number { return Math.max(0, n || 0); }

  fmtMonth(ym: string): string {
    return fmtMonth(ym, this.translate.currentLang);
  }

  fmtMonthShort(ym: string): string {
    return fmtMonthShort(ym, this.translate.currentLang);
  }
}
